import { apiClient } from './client.js';
import type { ApiResponse } from '../types/index.js';

export interface DevelopmentDefinition {
  upgrade_definition_id: string;
  name: string;
  description: string | null;
  category: string;
  stage_requirement: number;
  cost: number;
  construction_days: number;
  daily_effects: Record<string, unknown>;
  completion_effects: Record<string, unknown>;
  upkeep_cost: number;
  effects: Record<string, unknown>;
  placement_rules: Record<string, unknown>;
  unlock_rules: Record<string, unknown>;
  repeatability: string;
  max_repeats: number;
  active: boolean;
  unlocked: boolean;
  unlockedByStage: boolean;
  unlockReason?: string;
  purchased: boolean;
  hasConflict: boolean;
  prerequisiteNames: string[];
  unlockRequirements: string[];
  requirementSummary: string[];
  placementBiome: string;
  placementLayer: string;
  maxCount?: number;
  existingCount: number;
  tradeOffs: string[];
  totalCost: number;
  flavour_text?: string;
  synergy_with?: string[];
}

export interface ForecastData {
  snapshots: Array<{
    day: number;
    stats: Record<string, number>;
    environmental: { pollution: number };
    geography: Record<string, number>;
    life: { lifeChance: number };
    events: Array<{
      day: number;
      type: string;
      message: string;
      details?: Record<string, unknown>;
    }>;
  }>;
  statChanges: Record<string, number>;
  warnings: string[];
  synergies: string[];
  totalCost: number;
  constructionDays: number;
  events: Array<{
    day: number;
    type: string;
    message: string;
  }>;
}

export interface DevelopmentProject {
  project_id: string;
  planet_id: string;
  upgrade_definition_id: string;
  status: string;
  progress_days: number;
  total_construction_days: number;
  started_at: string;
  completed_at: string | null;
  cancelled_at: string | null;
  upgrade: DevelopmentDefinition;
}

export const developmentApi = {
  getDefinitions: () =>
    apiClient.get<ApiResponse<DevelopmentDefinition[]>>('/development/definitions').then((r) => r.data),

  getProjects: () =>
    apiClient.get<ApiResponse<{
      active: DevelopmentProject[];
      completed: DevelopmentProject[];
      cancelled: DevelopmentProject[];
      maxSlots: number;
      availableSlots: number;
      currentDay: number;
      dayDurationMs?: number;
    }>>('/development/projects').then((r) => r.data),

  startProject: (upgradeDefinitionId: string) =>
    apiClient.post<ApiResponse<{ project: DevelopmentProject; planet: any; balance: any }>>('/development/start', {
      upgradeDefinitionId,
    }).then((r) => r.data),

  cancelProject: (projectId: string) =>
    apiClient.post<ApiResponse<{ refund: number; planet: any; balance: any }>>('/development/cancel', {
      projectId,
    }).then((r) => r.data),

  completeProject: (projectId: string) =>
    apiClient.post<ApiResponse<{ planet: any; balance: any }>>('/development/complete', {
      projectId,
    }).then((r) => r.data),

  getForecast: (upgradeDefinitionId: string, days?: number) =>
    apiClient.get<ApiResponse<ForecastData>>('/development/forecast', {
      params: { upgradeDefinitionId, days: days || 7 },
    }).then((r) => r.data),

  tick: (days: number) =>
    apiClient.post<ApiResponse<{ planet: any; daysAdvanced: number; newDay: number; events: any[] }>>('/development/tick', {
      days,
    }).then((r) => r.data),
};
